import { EditOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import { Alert, Button, Checkbox, Col, Modal, Row, Skeleton, Space, Typography } from "antd";
import SaleChannelTags from "components/SaleChannelTags";
import { useGetInfoPlatform } from "hooks/info-platform";
import { useProductPermissions, useSetProductSaleChannels } from "hooks/product/product";
import { useLazySalesChannelsByPlatform } from "hooks/salesChannel";
import { isEqual, sortBy } from "lodash";
import { sortSaleChannels } from "pages/App/Product/constants";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { notify } from "utils/helperFuncs";
import "./index.scss";

const MODE = { VIEW: "view", EDIT: "edit" };

const toIds = (channels) =>
  Array.isArray(channels) ? channels.filter(Boolean).map((channel) => String(channel.id)) : [];

// Gửi id theo thứ tự R17 để request ổn định, không phụ thuộc thứ tự tick
const sortIds = (ids) => sortSaleChannels(ids.map((id) => ({ id }))).map((channel) => String(channel.id));

/**
 * Mục "Kênh bán" ở trang chi tiết SP: view mode (tag) + edit mode inline (checkbox lọc theo platform).
 * - Nút "Chỉnh sửa" chỉ render khi có quyền product_all / product_update (R9).
 * - Bỏ kênh thì mở modal xác nhận (R10); chỉ thêm kênh thì lưu ngay.
 * - Chỉ gửi 1 request dù bấm Lưu nhiều lần (R13).
 */
const SaleChannelSection = ({ product }) => {
  const { t } = useTranslation();
  const { canUpdateSaleChannels } = useProductPermissions();
  // Lọc kênh theo platform của SP (R1). Schema gateway (nhánh epic HAF_Alpha-10451) không có trường platform trên
  // type Product, nên dùng platform của phiên (platform { get { id } }): BE gán products.platform_id = platform của
  // user khi tạo SP và lọc danh sách SP theo platform đó, nên SP mở được ở web vận hành luôn cùng platform với phiên.
  // Nếu lệch, BE trả lỗi ProductSalesChannelPlatformMismatch và FE hiện nguyên văn.
  const { platformID, loading: platformLoading } = useGetInfoPlatform();
  const { load, refetch, called, loading, error, getChannelsByPlatform } = useLazySalesChannelsByPlatform();
  const { handleSetSaleChannels } = useSetProductSaleChannels();

  const [mode, setMode] = useState(MODE.VIEW);
  const [channels, setChannels] = useState(product?.saleChannels);
  const [initialIds, setInitialIds] = useState(() => toIds(product?.saleChannels));
  const [selectedIds, setSelectedIds] = useState(() => toIds(product?.saleChannels));
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  // Đang mở modal xác nhận thì không mở thêm modal thứ hai (bấm Lưu nhanh 2 lần), tránh gửi 2 request (R13)
  const confirmOpenRef = useRef(false);

  // Đồng bộ khi product (cache Apollo) đổi, chỉ lúc đang xem để không ghi đè lựa chọn đang sửa
  useEffect(() => {
    if (mode === MODE.VIEW) {
      setChannels(product?.saleChannels);
      setInitialIds(toIds(product?.saleChannels));
      setSelectedIds(toIds(product?.saleChannels));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.saleChannels]);

  const options = mode === MODE.EDIT ? getChannelsByPlatform(platformID) : [];
  const isLoadingOptions = !called || loading || platformLoading;
  const isEmptyOptions = !isLoadingOptions && !error && options.length === 0;
  const isDirty = !isEqual(sortBy(selectedIds), sortBy(initialIds));
  const removedIds = initialIds.filter((id) => !selectedIds.includes(id));
  const removedChannels = (channels || []).filter((channel) => removedIds.includes(String(channel?.id)));
  const canSave = isDirty && !isLoadingOptions && !error && !isEmptyOptions && !saving;

  const handleEdit = () => {
    setSelectedIds(initialIds);
    setMode(MODE.EDIT);
    if (!called) load();
  };

  const handleCancel = () => {
    if (saving) return;
    setSelectedIds(initialIds);
    setMode(MODE.VIEW);
  };

  const submit = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const res = await handleSetSaleChannels({ id: product?.id, saleChannelIDs: sortIds(selectedIds) });
      const newChannels = res?.data?.product?.setSaleChannels?.saleChannels;
      // Response thiếu setSaleChannels/saleChannels (BE trả null) thì coi là lỗi, không hiển thị như đã bỏ hết kênh
      if (!Array.isArray(newChannels)) {
        throw new Error(t("v2.product.detail.saleChannel.invalidResponse"));
      }
      notify.success({ message: t("v2.product.detail.saleChannel.saveSuccess") });
      setChannels(newChannels);
      setInitialIds(toIds(newChannels));
      setSelectedIds(toIds(newChannels));
      setMode(MODE.VIEW);
    } catch (err) {
      // Giữ edit mode và lựa chọn; hiện nguyên văn message BE
      notify.error({ message: t("common.error"), description: err?.message });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!canSave || savingRef.current || confirmOpenRef.current) return;
    if (removedIds.length === 0) {
      submit();
      return;
    }
    confirmOpenRef.current = true;
    Modal.confirm({
      className: "sale-channel-confirm-modal",
      icon: <ExclamationCircleFilled style={{ color: "#faad14" }} />,
      title: t("v2.product.detail.saleChannel.confirmTitle"),
      content: (
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Typography.Text>{t("v2.product.detail.saleChannel.confirmIntro")}</Typography.Text>
          <SaleChannelTags channels={removedChannels} />
          <Typography.Text>{t("v2.product.detail.saleChannel.confirmWarning")}</Typography.Text>
          {selectedIds.length === 0 && (
            <Typography.Text>
              {t("v2.product.detail.saleChannel.confirmNoChannelPrefix")}
              <Typography.Text strong>{t("v2.product.detail.saleChannel.confirmNoChannelBold")}</Typography.Text>.
            </Typography.Text>
          )}
        </Space>
      ),
      okText: t("v2.product.detail.saleChannel.confirmOk"),
      cancelText: t("v2.product.detail.saleChannel.cancel"),
      maskClosable: true,
      // Trả Promise để AntD giữ modal mở và hiện loading trên nút tới khi có kết quả; lỗi thì vẫn đóng modal, giữ edit mode
      onOk: () =>
        submit().finally(() => {
          confirmOpenRef.current = false;
        }),
      // Hủy, Esc, bấm ra ngoài: mở khoá để lần Lưu sau mở modal được
      onCancel: () => {
        confirmOpenRef.current = false;
      },
    });
  };

  const renderEditBody = () => {
    if (error) {
      return (
        <Alert
          type="error"
          showIcon
          message={t("v2.product.detail.saleChannel.loadError")}
          action={
            <Button size="small" onClick={() => refetch()}>
              {t("v2.product.detail.saleChannel.retry")}
            </Button>
          }
        />
      );
    }
    if (isLoadingOptions) {
      return <Skeleton active paragraph={{ rows: 3 }} title={false} />;
    }
    if (isEmptyOptions) {
      return <Typography.Text type="secondary">{t("v2.product.detail.saleChannel.emptyForPlatform")}</Typography.Text>;
    }
    return (
      <Checkbox.Group
        className="sale-channel-checkbox-group"
        value={selectedIds}
        onChange={(values) => setSelectedIds(values.map(String))}
        disabled={saving}
      >
        {options.map((channel) => (
          <div key={channel.id} className="sale-channel-checkbox-item">
            <Checkbox value={String(channel.id)}>{channel.name}</Checkbox>
          </div>
        ))}
      </Checkbox.Group>
    );
  };

  return (
    <Row className="sale-channel-section">
      <Col span={24}>
        <Space size={8}>
          <Typography.Text style={{ color: "#5B6673" }}>{t("v2.product.detail.sales_channel")}</Typography.Text>
          {canUpdateSaleChannels && mode === MODE.VIEW && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              className="sale-channel-edit-button"
              onClick={handleEdit}
            >
              {t("v2.product.detail.saleChannel.edit")}
            </Button>
          )}
        </Space>
      </Col>
      <Col span={24}>
        {mode === MODE.VIEW ? (
          <SaleChannelTags channels={channels} />
        ) : (
          <div className="sale-channel-edit">
            <Typography.Text type="secondary" className="sale-channel-edit-hint">
              {t("v2.product.detail.saleChannel.hint")}
            </Typography.Text>
            {renderEditBody()}
            <Space className="sale-channel-edit-footer">
              <Button size="small" onClick={handleCancel} disabled={saving} className="sale-channel-cancel-button">
                {t("v2.product.detail.saleChannel.cancel")}
              </Button>
              <Button
                size="small"
                type="primary"
                onClick={handleSave}
                disabled={!canSave}
                loading={saving}
                className="sale-channel-save-button"
              >
                {t("v2.product.detail.saleChannel.save")}
              </Button>
            </Space>
          </div>
        )}
      </Col>
    </Row>
  );
};

export default SaleChannelSection;
